import { useRef, useState } from 'react';
import { useCollection } from '@/hooks/useCollection';
import { uploadImage, createRow, deleteRow } from '@/lib/api';
import type { MediaItem } from '@/lib/types';

function titleFromName(name: string): string {
  const base = name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
  if (!base) return 'Photo';
  return base
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function MediaLibraryManager() {
  const { rows, loading, error, reload } = useCollection<MediaItem>('media_library', 'created_at', false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const inputClass =
    'w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900';

  const pick = (list: FileList | null) => {
    if (!list) return;
    setFiles(Array.from(list));
    setStatus('idle');
    setMessage('');
  };

  const uploadAll = async () => {
    if (files.length === 0) return;
    setStatus('uploading');
    setMessage('');
    try {
      let done = 0;
      for (const file of files) {
        const url = await uploadImage(file, 'media');
        await createRow('media_library', {
          url,
          title: titleFromName(file.name),
          category: category.trim() || 'General',
          sort_order: 0,
        });
        done += 1;
      }
      setStatus('done');
      setMessage(`${done} photo${done > 1 ? 's' : ''} added to the library.`);
      setFiles([]);
      if (inputRef.current) inputRef.current.value = '';
      reload();
    } catch (err) {
      setStatus('error');
      setMessage(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    }
  };

  const remove = async (id: string) => {
    setBusyId(id);
    try {
      await deleteRow('media_library', id);
      reload();
    } catch (err) {
      setStatus('error');
      setMessage(err instanceof Error ? err.message : 'Could not delete photo.');
    } finally {
      setBusyId(null);
    }
  };

  const copy = async (item: MediaItem) => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      setCopiedId(null);
    }
  };

  return (
    <section className="bg-white rounded-lg border border-background-200 p-6">
      <div className="flex items-center gap-2 mb-1">
        <i className="ri-image-2-line text-primary-600" aria-hidden="true" />
        <h3 className="font-heading font-semibold text-base text-foreground-900">Media Library</h3>
      </div>
      <p className="text-sm text-foreground-500 mb-4">
        Upload photos in bulk here. They are stored in your photo library and can be used anywhere on the website,
        including the gallery and the page images below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Photos</label>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full btn-outline justify-center whitespace-nowrap text-sm cursor-pointer"
          >
            <i className="ri-add-line" aria-hidden="true" />
            {files.length > 0 ? `${files.length} selected` : 'Choose Photos'}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => pick(e.target.files)}
          />
        </div>
        <div>
          <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Category (optional)</label>
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g. Worship, Outreach"
            className={inputClass}
          />
        </div>
      </div>

      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {files.map((f) => (
            <span key={f.name} className="text-xs bg-background-100 text-foreground-600 px-2 py-1 rounded-md">
              {f.name}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={uploadAll}
          disabled={files.length === 0 || status === 'uploading'}
          className="btn-primary whitespace-nowrap disabled:opacity-50 cursor-pointer"
        >
          {status === 'uploading' ? (
            <>
              <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
              Uploading...
            </>
          ) : (
            <>
              <i className="ri-upload-cloud-2-line" aria-hidden="true" />
              Upload All
            </>
          )}
        </button>
        {message && <p className={`text-sm ${status === 'error' ? 'text-red-600' : 'text-accent-700'}`}>{message}</p>}
      </div>

      <div className="border-t border-background-200 pt-5">
        {loading ? (
          <p className="text-center text-foreground-400 py-8">
            <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading library...
          </p>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-600 text-sm mb-3">Could not load the media library.</p>
            <button type="button" onClick={reload} className="btn-outline">Retry</button>
          </div>
        ) : rows.length === 0 ? (
          <p className="text-center text-foreground-400 py-8 text-sm">
            No photos yet. Upload your first batch above.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {rows.map((item) => (
              <div key={item.id} className="group relative rounded-lg overflow-hidden border border-background-200 bg-background-50">
                <div className="h-32 w-full overflow-hidden">
                  <img src={item.url} alt={item.title || 'Library photo'} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-2.5">
                  <p className="text-xs font-medium text-foreground-700 line-clamp-1" title={item.title || ''}>
                    {item.title || 'Untitled'}
                  </p>
                  {item.category && <p className="text-[11px] text-foreground-400 mt-0.5">{item.category}</p>}
                  <div className="flex items-center gap-1.5 mt-2">
                    <button
                      type="button"
                      onClick={() => copy(item)}
                      className="flex-1 text-[11px] font-label px-2 py-1 rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {copiedId === item.id ? 'Copied!' : 'Copy link'}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(item.id)}
                      disabled={busyId === item.id}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                      aria-label="Delete photo"
                    >
                      <i className={busyId === item.id ? 'ri-loader-4-line animate-spin' : 'ri-delete-bin-line'} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}